import React, { useState, useRef, useEffect } from 'react';
import { BiSolidDownArrow } from 'react-icons/bi';
import { IoMdCloseCircle } from 'react-icons/io';
import { getNearbyAirports, getAirportDisplayName, getCityDisplayForNearbyAirports, isDomesticUSARoute, getAvailableAirlines, getAvailableStopOptions } from '../../utils/flightSearch';

const FilterBar = ({ selectedClass, onClassChange, resultsCount = 27, stopsFilter, onStopsFilterChange, onClearFilters, fromCode, toCode, searchResults = [], isRedeemMiles = false }) => {
  const [showAirlinesDropdown, setShowAirlinesDropdown] = useState(false);
  const [showAirportsDropdown, setShowAirportsDropdown] = useState(false);
  const [showStopsDropdown, setShowStopsDropdown] = useState(false);
  // Get dynamic airlines based on route type
  const availableAirlines = getAvailableAirlines(fromCode || 'JFK', toCode || 'LAX');
  const isDomestic = isDomesticUSARoute(fromCode || 'JFK', toCode || 'LAX');
  
  // Get dynamic stop options based on search results
  const availableStopOptions = getAvailableStopOptions(searchResults);
  
  // Reset stops filter if current selection is not available
  useEffect(() => {
    if (!availableStopOptions.includes(stopsFilter)) {
      // Reset to first available option (usually "Nonstop only" or "Any number of stops")
      onStopsFilterChange(availableStopOptions[0] || 'Any number of stops');
    }
  }, [availableStopOptions, stopsFilter, onStopsFilterChange]);
  
  const [selectedAirlines, setSelectedAirlines] = useState(availableAirlines);
  // Get dynamic nearby airports based on search parameters
  const departureAirports = getNearbyAirports(fromCode || 'JFK');
  const arrivalAirports = getNearbyAirports(toCode || 'LAX');
  
  // Initialize state with all available airports selected by default
  const [selectedAirports, setSelectedAirports] = useState({
    departure: departureAirports.map(airport => getAirportDisplayName(airport)),
    arrival: arrivalAirports.map(airport => getAirportDisplayName(airport))
  });

  // Update selected airports when from/to codes change
  useEffect(() => {
    const newDepartureAirports = getNearbyAirports(fromCode || 'JFK');
    const newArrivalAirports = getNearbyAirports(toCode || 'LAX');
    
    setSelectedAirports({
      departure: newDepartureAirports.map(airport => getAirportDisplayName(airport)),
      arrival: newArrivalAirports.map(airport => getAirportDisplayName(airport))
    });
  }, [fromCode, toCode]);

  // Update selected airlines when route changes
  useEffect(() => {
    const newAvailableAirlines = getAvailableAirlines(fromCode || 'JFK', toCode || 'LAX');
    setSelectedAirlines(newAvailableAirlines);
  }, [fromCode, toCode]);

  const airlinesRef = useRef(null);
  const airportsRef = useRef(null);
  const stopsRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (airlinesRef.current && !airlinesRef.current.contains(event.target)) {
        setShowAirlinesDropdown(false);
      }
      if (airportsRef.current && !airportsRef.current.contains(event.target)) {
        setShowAirportsDropdown(false);
      }
      if (stopsRef.current && !stopsRef.current.contains(event.target)) {
        setShowStopsDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleStopsFilter = (filterValue) => {
    onStopsFilterChange(filterValue);
    setShowStopsDropdown(false);
  };

  const handleStopsClearFilter = (e) => {
    e.stopPropagation(); // Prevent dropdown from toggling
    onStopsFilterChange('Any number of stops');
    setShowStopsDropdown(false);
  };

  const handleAirlinesClearFilter = (e) => {
    e.stopPropagation(); // Prevent dropdown from toggling
    setSelectedAirlines(availableAirlines);
    setShowAirlinesDropdown(false);
  };

  const handleAirportsClearFilter = (e) => {
    e.stopPropagation(); // Prevent dropdown from toggling
    setSelectedAirports({
      departure: departureAirports.map(airport => getAirportDisplayName(airport)),
      arrival: arrivalAirports.map(airport => getAirportDisplayName(airport))
    });
    setShowAirportsDropdown(false);
  };

  // Combined handler for clear all filters
  const handleClearAllFilters = () => {
    // Reset local FilterBar state
    setSelectedAirlines(availableAirlines);
    setSelectedAirports({
      departure: departureAirports.map(airport => getAirportDisplayName(airport)),
      arrival: arrivalAirports.map(airport => getAirportDisplayName(airport))
    });
    setShowAirlinesDropdown(false);
    setShowAirportsDropdown(false);
    setShowStopsDropdown(false);
    
    // Call parent's clear filters (resets class and stops)
    onClearFilters();
  };

  const toggleAirline = (airline) => {
    setSelectedAirlines(prev => 
      prev.includes(airline) 
        ? prev.filter(a => a !== airline)
        : [...prev, airline]
    );
  };

  const toggleAirport = (type, airport) => {
    setSelectedAirports(prev => ({
      ...prev,
      [type]: prev[type].includes(airport)
        ? prev[type].filter(a => a !== airport)
        : [...prev[type], airport]
    }));
  };

  return (
    <div className={`mb-6 bg-white ${isRedeemMiles ? 'sticky top-0 z-30 border-b border-gray-200 shadow-sm' : ''}`}>
      <div className="flex justify-between items-center py-4 px-1">
        <div className="flex items-center space-x-4">
          <span className="text-gray-700 font-medium">Filter by:</span>
          
          {/* Stops dropdown */}
          <div className="relative" ref={stopsRef}>
            <button 
              onClick={() => setShowStopsDropdown(!showStopsDropdown)}
              className={`inline-flex items-center px-4 py-2 rounded-md border gap-3 transition-colors ${
                stopsFilter !== 'Any number of stops' 
                  ? 'border-[#0078d2] bg-[#e2effe] text-[#0078d2] hover:text-[#0078d2]' 
                  : 'border-[#0078d2] bg-white text-[#0078d2] hover:bg-[#f3f5f7] hover:text-[#0078d2]'
              }`}
            >
              {stopsFilter !== 'Any number of stops' && (
                <IoMdCloseCircle 
                  size={25} 
                  className="text-[#0061ab] hover:text-[#004e8a] cursor-pointer focus:outline-1 focus:outline-offset-1 focus:outline-[#0078d2]" 
                  onClick={handleStopsClearFilter}
                />
              )}
              <span className="text-sm font-medium hover:text-[#0078d2] hover:underline">
                {stopsFilter !== 'Any number of stops' ? stopsFilter : 'Stops'}
              </span>
              <BiSolidDownArrow size={13} />
            </button>

            {showStopsDropdown && (
              <div className="absolute top-full left-0 mt-2 w-80 bg-white rounded shadow-lg border border-gray-200 z-50 animate-in fade-in slide-in-from-top-2 duration-200">
                <div className="p-4">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Stops</h3>
                    <button 
                      onClick={() => setShowStopsDropdown(false)}
                      className="text-[#0078d2] hover:text-[#0078d2] font-semibold text-sm"
                    >
                      Done
                    </button>
                  </div>
                  
                  <div className="space-y-3">
                    {availableStopOptions.map((option) => (
                      <label key={option} className="flex items-center space-x-3 cursor-pointer">
                        <input
                          type="radio"
                          name="stops"
                          value={option}
                          checked={stopsFilter === option}
                          onChange={(e) => handleStopsFilter(e.target.value)}
                          className="w-5 h-5 text-[#0078d2] border-2 border-gray-300 rounded focus:ring-[#0078d2]"
                          style={{ accentColor: '#0078d2' }}
                        />
                        <span className="text-gray-700 font-medium">{option}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Airlines dropdown */}
          <div className="relative" ref={airlinesRef}>
            <button 
              onClick={() => !isDomestic && setShowAirlinesDropdown(!showAirlinesDropdown)}
              className={`inline-flex items-center px-4 py-2 rounded-md border gap-3 transition-colors ${
                isDomestic
                  ? 'border-gray-300 bg-gray-100 text-gray-600 cursor-default'
                  : selectedAirlines.length < availableAirlines.length 
                    ? 'border-[#0078d2] bg-[#e2effe] text-[#0078d2]  hover:text-[#0078d2]' 
                    : 'border-[#0078d2] bg-white text-[#0078d2] hover:bg-[#f3f5f7] hover:text-[#0078d2]'
              }`}
              disabled={isDomestic}
            >
              {!isDomestic && selectedAirlines.length < availableAirlines.length && (
                <IoMdCloseCircle 
                  size={25} 
                  className="text-[#0061ab] hover:text-[#004e8a] cursor-pointer" 
                  onClick={handleAirlinesClearFilter}
                />
              )}
              <span className="text-sm font-medium">
                {isDomestic 
                  ? 'American Airlines'
                  : selectedAirlines.length < availableAirlines.length 
                    ? `${selectedAirlines.length} airline${selectedAirlines.length !== 1 ? 's' : ''} selected`
                    : 'Airlines'
                }
              </span>
              {!isDomestic && <BiSolidDownArrow size={13} />}
            </button>

            {showAirlinesDropdown && !isDomestic && (
              <div className="absolute top-full left-0 mt-2 w-80 bg-white rounded shadow-lg border border-gray-200 z-50 animate-in fade-in slide-in-from-top-2 duration-200">
                <div className="p-4">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Airlines</h3>
                    <button 
                      onClick={() => setShowAirlinesDropdown(false)}
                      className="text-[#0078d2] hover:text-[#0078d2] font-semibold text-sm"
                    >
                      Done
                    </button>
                  </div>
                  
                  <div className="space-y-3">
                    {availableAirlines.map((airline) => (
                      <label key={airline} className="flex items-center space-x-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedAirlines.includes(airline)}
                          onChange={() => toggleAirline(airline)}
                          className="w-5 h-5 text-[#0078d2] border-2 border-gray-300 rounded focus:ring-[#0078d2]"
                          style={{ accentColor: '#0078d2' }}
                        />
                        <span className="text-gray-700 font-medium">{airline}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Nearby airports dropdown */}
          <div className="relative" ref={airportsRef}>
            <button 
              onClick={() => setShowAirportsDropdown(!showAirportsDropdown)}
              className={`inline-flex items-center px-4 py-2 rounded-md border gap-3 transition-colors ${
                (selectedAirports.departure.length < departureAirports.length || selectedAirports.arrival.length < arrivalAirports.length)
                  ? 'border-[#0078d2] bg-[#e2effe] text-[#0078d2]  hover:text-[#0078d2]' 
                  : 'border-[#0078d2] bg-white text-[#0078d2] hover:bg-[#f3f5f7] hover:text-[#0078d2]'
              }`}
            >
              {(selectedAirports.departure.length < departureAirports.length || selectedAirports.arrival.length < arrivalAirports.length) && (
                <IoMdCloseCircle 
                  size={25} 
                  className="text-[#0061ab] hover:text-[#004e8a] cursor-pointer" 
                  onClick={handleAirportsClearFilter}
                />
              )}
              <span className="text-sm font-medium hover:text-[#0078d2] hover:underline">
                {(() => {
                  const totalSelected = selectedAirports.departure.length + selectedAirports.arrival.length;
                  const totalAvailable = departureAirports.length + arrivalAirports.length;
                  
                  if (totalSelected === 0) {
                    return 'No airports selected';
                  } else if (totalSelected < totalAvailable) {
                    return `${totalSelected} airport${totalSelected !== 1 ? 's' : ''} selected`;
                  } else {
                    return 'Nearby airports';
                  }
                })()}
              </span>
              <BiSolidDownArrow size={13} />
            </button>

            {showAirportsDropdown && (
              <div className="absolute top-full left-0 mt-2 w-96 bg-white rounded shadow-lg border border-gray-200 z-50 animate-in fade-in slide-in-from-top-2 duration-200">
                <div className="p-4">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Nearby airports</h3>
                    <button 
                      onClick={() => setShowAirportsDropdown(false)}
                      className="text-[#0078d2] hover:text-[#0078d2] font-semibold text-sm"
                    >
                      Done
                    </button>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <h4 className="text-sm font-medium text-gray-600 mb-2">
                        Depart from {getCityDisplayForNearbyAirports(fromCode)}:
                      </h4>
                      <div className="space-y-2">
                        {departureAirports.map((airport) => {
                          const displayName = getAirportDisplayName(airport);
                          return (
                            <label key={airport.code} className="flex items-center space-x-3 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={selectedAirports.departure.includes(displayName)}
                                onChange={() => toggleAirport('departure', displayName)}
                                className="w-5 h-5 text-[#0078d2] border-2 border-gray-300 rounded focus:ring-[#0078d2]"
                                style={{ accentColor: '#0078d2' }}
                              />
                              <span className="text-gray-700">{displayName}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>

                    <div>
                      <h4 className="text-sm font-medium text-gray-600 mb-2">
                        Arrive in {getCityDisplayForNearbyAirports(toCode)}:
                      </h4>
                      <div className="space-y-2">
                        {arrivalAirports.map((airport) => {
                          const displayName = getAirportDisplayName(airport);
                          return (
                            <label key={airport.code} className="flex items-center space-x-3 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={selectedAirports.arrival.includes(displayName)}
                                onChange={() => toggleAirport('arrival', displayName)}
                                className="w-5 h-5 text-[#0078d2] border-2 border-gray-300 rounded focus:ring-[#0078d2]"
                                style={{ accentColor: '#0078d2' }}
                              />
                              <span className="text-gray-700">{displayName}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-4">
          {(stopsFilter !== 'Any number of stops' || (!isDomestic && selectedAirlines.length < availableAirlines.length) || (selectedAirports.departure.length + selectedAirports.arrival.length) < (departureAirports.length + arrivalAirports.length)) && (
            <button
              onClick={handleClearAllFilters}
              className="text-[#0078d2] text-sm font-medium hover:underline"
            >
              Clear filters
            </button>
          )}
          <div className="text-gray-700 font-medium">{resultsCount} results</div>
        </div>
      </div>
    </div>
  );
};

export default FilterBar;
